import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../api/auth.api';
import { User, UserRole } from '../gen/proto/api/gateway/v1/user_pb';
import useTenantStore from './tenant.store';

const ROLE_MAP: Record<string, UserRole> = {
  super_admin: UserRole.SUPER_ADMIN,
  company_admin: UserRole.COMPANY_ADMIN,
  admin: UserRole.ADMIN,
  user: UserRole.USER,
};

const parseRoleFromToken = (token: string): UserRole => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const roles: string[] = payload?.realm_access?.roles ?? [];
    for (const r of roles) {
      if (r in ROLE_MAP) return ROLE_MAP[r];
    }
  } catch {}
  return UserRole.UNSPECIFIED;
};

const getRoleFlags = (role?: UserRole) => ({
  isAdmin: role === UserRole.ADMIN,
  isCompanyAdmin: role === UserRole.COMPANY_ADMIN,
  isSuperAdmin: role === UserRole.SUPER_ADMIN,
  isUser: role === UserRole.USER,
  isSuperAdminOrCompanyAdmin: role === UserRole.COMPANY_ADMIN || role === UserRole.SUPER_ADMIN,
  isSuperAdminOrCompanyAdminOrAdmin:
    role === UserRole.COMPANY_ADMIN || role === UserRole.SUPER_ADMIN || role === UserRole.ADMIN,
});

interface AuthStore {
  authToken?: string;
  refreshToken?: string;
  uuid?: string;
  tenantUuid?: string;
  email?: string;
  username: string;
  role?: UserRole;
  isAdmin: boolean;
  isCompanyAdmin: boolean;
  isSuperAdmin: boolean;
  isUser: boolean;
  isSuperAdminOrCompanyAdmin: boolean;
  isSuperAdminOrCompanyAdminOrAdmin: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (username: string, password: string, email: string) => Promise<void>;
  checkToken: () => Promise<boolean>;
  setAuthToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  setUser: (user: User) => void;
  setUuid: (uuid: string) => void;
  setEmail: (email: string) => void;
  setUsername: (username: string) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setRole: (role: UserRole) => void;
}

const decodeJwt = (token: string): Record<string, string> => {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      authToken: undefined,
      refreshToken: undefined,
      uuid: undefined,
      user: undefined,
      email: undefined,
      username: '',
      role: undefined,
      ...getRoleFlags(undefined),
      isAuthenticated: false,

      login: async (username: string, password: string) => {
        const response = await authApi.login({
          username,
          password,
        });
        const payload = decodeJwt(response.accessToken);
        const role = parseRoleFromToken(response.accessToken);
        set({
          username: payload.name,
          email: payload.email,
          uuid: payload.sub,
          tenantUuid: payload.tenant_uuid,
          authToken: response.accessToken,
          refreshToken: response.refreshToken,
          role,
          ...getRoleFlags(role),
          isAuthenticated: true,
        });
      },

      logout: async () => {
        const { refreshToken } = get();
        if (refreshToken) {
          await authApi.logout(refreshToken);
        }
        set({
          authToken: undefined,
          refreshToken: undefined,
          uuid: undefined,
          email: undefined,
          username: undefined,
          role: undefined,
          ...getRoleFlags(undefined),
          isAuthenticated: false,
        });
        useTenantStore.getState().resetTenant();
      },

      register: async (username: string, password: string, email: string) => {
        const response = await authApi.register({
          username,
          password,
          email,
        });
        const role = parseRoleFromToken(response.accessToken);
        set({
          username: username,
          authToken: response.accessToken,
          refreshToken: response.refreshToken,
          role,
          ...getRoleFlags(role),
          isAuthenticated: true,
        });
      },

      checkToken: async () => {
        const { authToken } = get();
        if (!authToken) return false;

        const response = await authApi.checkToken(authToken);
        if (response.tokenValid) {
          const payload = decodeJwt(authToken);
          const role = parseRoleFromToken(authToken);
          set({
            email: response.email,
            role,
            ...getRoleFlags(role),
            username: payload.name,
            uuid: payload.sub,
            tenantUuid: payload.tenant_uuid,
            isAuthenticated: true,
          });
          return true;
        }

        set({
          authToken: undefined,
          refreshToken: undefined,
          uuid: undefined,
          email: undefined,
          username: undefined,
          role: undefined,
          ...getRoleFlags(undefined),
          isAuthenticated: false,
        });
        return false;
      },

      setAuthToken: (token: string) => set({ authToken: token }),
      setRefreshToken: (refreshToken: string) => set({ refreshToken }),
      setUuid: (uuid: string) => set({ uuid }),
      setEmail: (email: string) => set({ email }),
      setUsername: (username: string) => set({ username }),
      setIsAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),
      setRole: (role: UserRole) => set({ role, ...getRoleFlags(role) }),

      setUser: (user: User) => {
        set({
          uuid: user.uuid,
          email: user.email,
          username: user.name,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        authToken: state.authToken,
        refreshToken: state.refreshToken,
        email: state.email,
        username: state.username,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.authToken) {
          const payload = decodeJwt(state.authToken);
          const role = parseRoleFromToken(state.authToken);
          state.uuid = payload.sub;
          state.tenantUuid = payload.tenant_uuid;
          state.role = role;
          const flags = getRoleFlags(role);
          state.isAdmin = flags.isAdmin;
          state.isCompanyAdmin = flags.isCompanyAdmin;
          state.isSuperAdmin = flags.isSuperAdmin;
          state.isUser = flags.isUser;
          state.isSuperAdminOrCompanyAdmin = flags.isSuperAdminOrCompanyAdmin;
          state.isSuperAdminOrCompanyAdminOrAdmin = flags.isSuperAdminOrCompanyAdminOrAdmin;
          state.isAuthenticated = true;
        }
      },
    },
  ),
);

export default useAuthStore;
